import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  List,
  ListItem,
  Divider,
  useTheme,
  useMediaQuery,
  LinearProgress,
} from '@mui/material';
import { Close, Keyboard, Mic, CheckCircle } from '@mui/icons-material';
import { useSettings } from '@/context/SettingsContext';
import { analyticsService } from '@/services/analyticsService';
import { SessionResultsData } from '@/types/analytics';

interface ResultsDialogProps {
  open: boolean;
  onClose: () => void;
  sessionId: string | null;
}

const ResultsDialog: React.FC<ResultsDialogProps> = ({ open, onClose, sessionId }) => {
  const { language } = useSettings();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultsData, setResultsData] = useState<SessionResultsData | null>(null);

  const translations = {
    en: {
      title: 'Survey Results',
      close: 'Close',
      loading: 'Loading results...',
      error: 'Failed to load results',
      retry: 'Retry',
      processing: 'Results are being processed. Please try again in a moment.',
      noSessionId: 'Session ID not available',
      npsScore: 'NPS Score',
      csatScore: 'CSAT Score',
      sentiment: 'Overall Sentiment',
      yourResponses: 'Your Responses',
      noResponses: 'No responses recorded',
      promoter: 'Promoter',
      passive: 'Passive',
      detractor: 'Detractor',
      positive: 'Positive',
      neutral: 'Neutral',
      negative: 'Negative',
      veryPositive: 'Very Positive',
      veryNegative: 'Very Negative',
      notCalculated: 'Not calculated yet',
      outOf: 'out of',
      mappedAnswer: 'Mapped Answer',
      enteredText: 'Entered Text',
      selectedOption: 'Selected Option',
      transcribedAudio: 'Transcribed Audio',
      question: 'Question',
    },
    ar: {
      title: 'نتائج الاستبيان',
      close: 'إغلاق',
      loading: 'جاري تحميل النتائج...',
      error: 'فشل تحميل النتائج',
      retry: 'إعادة المحاولة',
      processing: 'جاري معالجة النتائج. يرجى المحاولة مرة أخرى بعد لحظات.',
      noSessionId: 'معرف الجلسة غير متاح',
      npsScore: 'درجة الترشيح',
      csatScore: 'درجة الرضا',
      sentiment: 'التقييم العام',
      yourResponses: 'إجاباتك',
      noResponses: 'لا توجد إجابات مسجلة',
      promoter: 'مؤيد',
      passive: 'محايد',
      detractor: 'غير راضٍ',
      positive: 'إيجابي',
      neutral: 'محايد',
      mappedAnswer: 'الإجابة المعالجة',
      enteredText: 'النص المدخل',
      selectedOption: 'الخيار المختار',
      transcribedAudio: 'النص المفرغ من الصوت',
      question: 'السؤال',
      negative: 'سلبي',
      veryPositive: 'إيجابي جداً',
      veryNegative: 'سلبي جداً',
      notCalculated: 'لم يتم الحساب بعد',
      outOf: 'من',
    },
  };

  const t = translations[language];

  useEffect(() => {
    if (open && sessionId) {
      fetchResults();
    }
  }, [open, sessionId]);

  const fetchResults = async () => {
    if (!sessionId) {
      setError(t.noSessionId);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await analyticsService.getSessionResults(sessionId);
      setResultsData(data);
    } catch (err) {
      console.error('Error fetching results:', err);
      setError(err instanceof Error ? err.message : t.error);
    } finally {
      setLoading(false);
    }
  };

  const getNPSCategory = (score: number | null) => {
    if (score === null) return null;
    if (score >= 9) return { label: t.promoter, color: 'success' as const };
    if (score >= 7) return { label: t.passive, color: 'warning' as const };
    return { label: t.detractor, color: 'error' as const };
  };

  const getSentimentCategory = (score: number | null) => {
    if (score === null) return null;
    if (score > 0.5) return { label: t.veryPositive, color: 'success' as const, emoji: '😊' };
    if (score > 0.25) return { label: t.positive, color: 'success' as const, emoji: '🙂' };
    if (score > -0.25) return { label: t.neutral, color: 'info' as const, emoji: '😐' };
    if (score > -0.5) return { label: t.negative, color: 'error' as const, emoji: '😞' };
    return { label: t.veryNegative, color: 'error' as const, emoji: '😢' };
  };

  const getCSATPercentage = (score: number | null) => {
    if (score === null) return 0;
    return (score / 5.0) * 100;
  };

  const getInputTypeIcon = (inputType?: string) => {
    switch (inputType) {
      case 'audio':
        return <Mic fontSize="small" sx={{ color: 'primary.main' }} />;
      case 'chosen':
        return <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />;
      case 'text':
      default:
        return <Keyboard fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
  };

  const handleRetry = () => {
    fetchResults();
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
          <CircularProgress size={48} />
          <Typography variant="body1" sx={{ mt: 2 }}>
            {t.loading}
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Box sx={{ py: 2 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button variant="outlined" onClick={handleRetry} fullWidth>
            {t.retry}
          </Button>
        </Box>
      );
    }

    if (!resultsData) {
      return (
        <Alert severity="info">{t.processing}</Alert>
      );
    }

    const { metrics } = resultsData;
    const npsCategory = getNPSCategory(metrics.nps_score);
    const sentimentCategory = getSentimentCategory(metrics.sentiment_score);
    const csatPercentage = getCSATPercentage(metrics.csat_score);

    return (
      <Box>
        {/* Metrics Cards */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          mb: 3
        }}>
          {/* NPS Score Card */}
          <Card elevation={2} sx={{ flex: 1 }}>
            <CardContent sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              minHeight: '200px',
              height: '100%'
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t.npsScore}
              </Typography>
                {metrics.nps_score !== null ? (
                  <>
                    <Typography variant="h3" sx={{ my: 1 }}>
                      {metrics.nps_score}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.outOf} 10
                    </Typography>
                    {npsCategory && (
                      <Box sx={{ mt: 2 }}>
                        <Chip
                          label={npsCategory.label}
                          color={npsCategory.color}
                          size="small"
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t.notCalculated}
                  </Typography>
                )}
              </CardContent>
            </Card>

          {/* CSAT Score Card */}
          <Card elevation={2} sx={{ flex: 1 }}>
            <CardContent sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              minHeight: '200px',
              height: '100%'
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t.csatScore}
              </Typography>
                {metrics.csat_score !== null ? (
                  <>
                    <Typography variant="h3" sx={{ my: 1 }}>
                      {metrics.csat_score.toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t.outOf} 5
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={csatPercentage}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {csatPercentage.toFixed(0)}%
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t.notCalculated}
                  </Typography>
                )}
              </CardContent>
            </Card>

          {/* Sentiment Card */}
          <Card elevation={2} sx={{ flex: 1 }}>
            <CardContent sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              minHeight: '200px',
              height: '100%'
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t.sentiment}
              </Typography>
                {metrics.sentiment_score !== null ? (
                  <>
                    <Typography variant="h3" sx={{ my: 1 }}>
                      {sentimentCategory?.emoji || '😐'}
                    </Typography>
                    {sentimentCategory && (
                      <Chip
                        label={sentimentCategory.label}
                        color={sentimentCategory.color}
                        size="small"
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {metrics.sentiment_score.toFixed(2)}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t.notCalculated}
                  </Typography>
                )}
              </CardContent>
            </Card>
        </Box>

        {/* Question-Answer Pairs Section */}
        <Box sx={{ direction: language === 'ar' ? 'rtl' : 'ltr', mt: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ textAlign: language === 'ar' ? 'right' : 'left' }}>
            {t.yourResponses}
          </Typography>

          {resultsData.result_payload && resultsData.result_payload.length > 0 ? (
            <List disablePadding>
              {resultsData.result_payload.map((item, index) => (
                <React.Fragment key={item.question_name}>
                  <ListItem
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      gap: 2.5,
                      py: 3,
                      px: { xs: 2, sm: 3 },
                      bgcolor: 'background.default',
                      borderRadius: 2,
                      my: 1,
                      boxShadow: 1,
                    }}
                  >
                    {/* Question Title */}
                    <Box sx={{ width: '100%' }}>
                      <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
                        {item.question_title}
                      </Typography>
                    </Box>

                    {/* What user said / transcribed / selected */}
                    <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {getInputTypeIcon(item.input_type)}
                      <Typography variant="body2" color="text.secondary">
                        {item.input_type === 'audio'
                          ? t.transcribedAudio
                          : item.input_type === 'chosen'
                          ? t.selectedOption
                          : t.enteredText}
                        :
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontStyle: 'italic',
                          color: 'text.primary',
                          flex: 1,
                          textAlign: language === 'ar' ? 'right' : 'left',
                        }}
                      >
                        "{item.received}"
                      </Typography>
                    </Box>

                    {/* Mapped/Final Answer */}
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <Box
                        sx={{
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          borderRadius: 2,
                          p: 2,
                          textAlign: 'center',
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {typeof item.answer === 'object' ? JSON.stringify(item.answer) : item.answer}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Optional: Confidence badge */}
                    {item.confidence && item.confidence !== 'high' && (
                      <Chip
                        label={item.confidence === 'medium' ? 'Medium confidence' : 'Low confidence'}
                        color={item.confidence === 'medium' ? 'warning' : 'error'}
                        size="small"
                      />
                    )}
                  </ListItem>

                  {index < resultsData.result_payload.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Alert severity="info">{t.noResponses}</Alert>
          )}
        </Box>        
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          direction: language === 'ar' ? 'rtl' : 'ltr',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{t.title}</Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          {t.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResultsDialog;